import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import adminNotificationsService from "../../services/adminNotificationsService";
import { useAdminNotificationUnread } from "../Context/notificationContext";
import { useUI } from "../Context/UIContext";
import ConfirmationModal from "../Shared/confirmationModal";
import CardNotifiche from "./CardNotifiche";
import DialogNotifiche from "./DialogNotifiche";

/* --- Mappers & type labels (pure; used only by this dashboard block) --- */

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function normalizeCreatedAt(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw) && raw.length >= 3) {
    const [y, mo, d, h = 0, mi = 0, s = 0] = raw;
    const dt = new Date(y, mo - 1, d, h, mi, s);
    return dt.toISOString();
  }
  return null;
}

export function formatNotifDate(raw) {
  const n = normalizeCreatedAt(raw);
  if (!n) return "";
  const dt = new Date(n);
  if (Number.isNaN(dt.getTime())) return "";
  return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
}

export function mapNotificationItem(raw) {
  const state = raw.state;
  const archived = state === "archived";
  const isRead = state === "read" || state === "archived";
  return {
    id: raw.id,
    title: raw.title,
    description: raw.content ?? "",
    date: formatNotifDate(raw.createdAt),
    isRead,
    archived,
    notificationType: raw.type ?? "",
  };
}

export const ADMIN_NOTIFICATION_TYPE_LABELS = {
  "assistance-ticket": "Assistenza",
  "vendor-registration": "Registrazione",
  "catalog-import": "Importazioni",
  // "budget": "Budget", 
};

export function labelForAdminNotificationType(type) {
  if (!type) return "";
  return ADMIN_NOTIFICATION_TYPE_LABELS[type] || type;
}

const TYPE_FILTER_ENTRIES = Object.entries(ADMIN_NOTIFICATION_TYPE_LABELS).map(([key, label]) => ({
  typeKey: key,
  label,
}));

const PAGE_SIZE = 10;

/** Scroll area height tuned for ~5 notification cards visible, then scrollbar. */
const NOTIFICHE_LIST_MAX_HEIGHT_PX = 620;

const navLinkSx = {
  fontFamily: "Rubik",
  fontWeight: "bold",
  color: "#0b3d87",
  cursor: "pointer",
  border: "none",
  background: "none",
  fontSize: 15,
  p: 0,
  transition: "font-weight 0.15s, color 0.15s",
  "&:hover": {
    fontWeight: 900,
    color: "#e00036",
  },
};

function mergeById(existing, batch) {
  const seen = new Set(existing.map((x) => String(x.id)));
  const out = [...existing];
  for (const row of batch) {
    const id = String(row.id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(row);
  }
  return out;
}

// Rimuove dalla lista la notifica con l'id indicato
function removeById(list, id) {
  return list.filter((x) => String(x.id) !== String(id));
}

// True quando non ci sono altre notifiche da caricare
function computeNoMore(totalCount, itemsLength, isInitialLoading) {
  return totalCount > 0 ? itemsLength >= totalCount : itemsLength === 0 && !isInitialLoading;
}

// Stato disabilitato del pulsante "Carica altro"
function computeLoadMoreDisabled(isInitialLoading, isLoadingMore, noMore, totalCount, itemsLength) {
  return isInitialLoading || isLoadingMore || noMore || (totalCount > 0 && itemsLength >= totalCount);
}

function handleCaughtError(e, showError, fallbackMessage) {
  if (e?.response != null) return;
  showError(e?.message || fallbackMessage);
}

// Pannello con stato di caricamento, vuoto o elenco notifiche
const NotificheListPanel = ({
  items,
  variant,
  isInitialLoading,
  isLoadingMore,
  loadMoreDisabled,
  emptyMessage,
  onOpen,
  onToggleRead,
  onArchive,
  onDelete,
  onRestore,
  onLoadMore,
}) => {
  if (isInitialLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress sx={{ color: "#0b3d87" }} size={32} />
      </Box>
    );
  }
  if (items.length === 0) {
    return <Typography sx={{ color: "#64748b", textAlign: "center", py: 4 }}>{emptyMessage}</Typography>;
  }
  return (
    <Box
      sx={{
        maxHeight: NOTIFICHE_LIST_MAX_HEIGHT_PX,
        overflowY: "auto",
        overflowX: "hidden",
        borderRadius: "15px",
        pr: 0.5,
        mr: -0.5,
      }}
    >
      <Box display="flex" flexDirection="column" gap={2} pb={1}>
        {items.map((n) => (
          <CardNotifiche
            key={n.id}
            notification={n}
            typeLabel={labelForAdminNotificationType(n.notificationType)}
            variant={variant}
            onOpen={onOpen}
            onToggleRead={onToggleRead}
            onArchive={onArchive}
            onDelete={onDelete}
            onRestore={onRestore}
            readOnly={false}
          />
        ))}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 2,
          gap: 1,
          alignItems: "center",
          flexDirection: "column",
          pt: 1,
          borderTop: "1px solid #e2e8f0",
        }}
      >
        {isLoadingMore ? <CircularProgress size={28} sx={{ color: "#0b3d87" }} /> : null}
        <Button
          variant="outlined"
          disabled={loadMoreDisabled}
          onClick={() => onLoadMore()}
          sx={{
            fontFamily: "inherit",
            textTransform: "none",
            borderRadius: "999px",
            px: 3,
            fontWeight: 700,
            borderColor: "#0b3d87",
            color: "#0b3d87",
            "&:hover": {
              borderColor: "#092b6a",
              backgroundColor: "#e8f0ff",
            },
          }}
        >
          {loadMoreDisabled && !isInitialLoading && items.length > 0
            ? "Non ci sono altre notifiche"
            : "Carica altro"}
        </Button>
      </Box>
    </Box>
  );
};

NotificheListPanel.propTypes = {
  items: PropTypes.array.isRequired,
  variant: PropTypes.string,
  isInitialLoading: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  loadMoreDisabled: PropTypes.bool,
  emptyMessage: PropTypes.string,
  onOpen: PropTypes.func.isRequired,
  onToggleRead: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
};

export default function Notifiche() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { refreshUnread } = useAdminNotificationUnread();
  const { showError } = useUI();

  const [viewArchived, setViewArchived] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(1);

  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [dialogNotif, setDialogNotif] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const listStateParam = viewArchived ? "archived" : undefined;

  const listBasePayload = useMemo(
    () => ({
      size: PAGE_SIZE,
      state: listStateParam,
      type: typeFilter || undefined,
    }),
    [listStateParam, typeFilter]
  );

  const closeDialogIfId = useCallback((id) => {
    setDialogNotif((cur) => (cur && String(cur.id) === String(id) ? null : cur));
  }, []);

  const loadInitial = useCallback(async () => {
    setIsInitialLoading(true);
    try {
      const res = await adminNotificationsService.list({
        page: 0,
        ...listBasePayload,
      });
      const mapped = (res.items ?? []).map(mapNotificationItem);
      setItems(mapped);
      setTotalCount(res.totalCount ?? 0);
      setNextPage(1);
      refreshUnread();
    } catch (e) {
      console.error("admin notifications list:", e);
      setItems([]);
      setTotalCount(0);
      handleCaughtError(e, showError, "Impossibile caricare le notifiche.");
    } finally {
      setIsInitialLoading(false);
    }
  }, [listBasePayload, refreshUnread, showError]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore) return;
    if (totalCount > 0 && items.length >= totalCount) return;

    setIsLoadingMore(true);
    try {
      const res = await adminNotificationsService.list({
        page: nextPage,
        ...listBasePayload,
      });
      const batch = (res.items ?? []).map(mapNotificationItem);
      if (batch.length === 0) {
        setTotalCount(items.length);
        return;
      }
      const pageTotal = res.totalCount ?? totalCount;
      if (pageTotal > 0) {
        setTotalCount(pageTotal);
      }
      setItems((prev) => mergeById(prev, batch));
      setNextPage((p) => p + 1);
    } catch (e) {
      console.error("admin notifications load more:", e);
      handleCaughtError(e, showError, "Impossibile caricare altri risultati.");
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, totalCount, items.length, nextPage, listBasePayload, showError]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const noMoreResults = computeNoMore(totalCount, items.length, isInitialLoading);
  const loadMoreDisabled = computeLoadMoreDisabled(
    isInitialLoading,
    isLoadingMore,
    noMoreResults,
    totalCount,
    items.length
  );

  const openDialog = useCallback(
    (n) => {
      if (!n.archived && !n.isRead) {
        const updated = { ...n, isRead: true };
        setDialogNotif(updated);
        setItems((prev) =>
          prev.map((x) => (String(x.id) === String(n.id) ? { ...x, isRead: true } : x))
        );
        adminNotificationsService
          .state({ notificationIds: [String(n.id)], state: "read" })
          .then(() => refreshUnread())
          .catch((e) => {
            handleCaughtError(e, showError, "Impossibile aggiornare la notifica.");
          });
      } else {
        setDialogNotif(n);
      }
    },
    [refreshUnread, showError]
  );

  const handleToggleRead = useCallback(
    (id) => {
      const target = items.find((x) => String(x.id) === String(id));
      if (!target) return;
      const willBeRead = !target.isRead;
      const nextState = willBeRead ? "read" : "not-read";
      setItems((prev) =>
        prev.map((x) =>
          String(x.id) === String(id) ? { ...x, isRead: willBeRead } : x
        )
      );
      setDialogNotif((cur) =>
        cur && String(cur.id) === String(id) ? { ...cur, isRead: willBeRead } : cur
      );
      adminNotificationsService
        .state({ notificationIds: [String(id)], state: nextState })
        .then(() => refreshUnread())
        .catch((e) => {
          handleCaughtError(e, showError, "Impossibile aggiornare lo stato di lettura.");
          loadInitial();
        });
    },
    [items, refreshUnread, showError, loadInitial]
  );

  const handleArchive = useCallback(
    (id) => {
      setItems((prev) => removeById(prev, id));
      closeDialogIfId(id);
      adminNotificationsService
        .state({ notificationIds: [String(id)], state: "archived" })
        .then(() => refreshUnread())
        .catch((e) => {
          handleCaughtError(e, showError, "Impossibile archiviare la notifica.");
          loadInitial();
        });
      setTotalCount((prev) => Math.max(0, prev - 1));
    },
    [refreshUnread, showError, loadInitial, closeDialogIfId]
  );

  const handleRestore = useCallback(
    (id) => {
      setItems((prev) => removeById(prev, id));
      closeDialogIfId(id);
      adminNotificationsService
        .state({ notificationIds: [String(id)], state: "read" })
        .then(() => refreshUnread())
        .catch((e) => {
          handleCaughtError(e, showError, "Impossibile ripristinare la notifica.");
          loadInitial();
        });
      setTotalCount((prev) => Math.max(0, prev - 1));
    },
    [refreshUnread, showError, loadInitial, closeDialogIfId]
  );

  const confirmDelete = useCallback(
    (id) => {
      adminNotificationsService
        .state({ notificationIds: [String(id)], state: "deleted" })
        .then(() => {
          setItems((prev) => removeById(prev, id));
          closeDialogIfId(id);
          setDeleteId(null);
          refreshUnread();
        })
        .catch((e) => {
          handleCaughtError(e, showError, "Impossibile eliminare la notifica.");
        });
      setTotalCount((prev) => Math.max(0, prev - 1));
    },
    [refreshUnread, showError, closeDialogIfId]
  );

  const variant = viewArchived ? "archived" : "active";

  const emptyMessage = viewArchived
    ? "Non ci sono notifiche archiviate."
    : "Non ci sono notifiche da mostrare.";

  const filteredForDisplay = items;

  const dialogTypeLabel = dialogNotif
    ? labelForAdminNotificationType(dialogNotif.notificationType)
    : "";

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="subtitle1"
        sx={{
          color: "#0b3d87",
          fontWeight: 800,
          mb: 1,
          ml: 1,
          textTransform: "uppercase",
          fontSize: "0.85rem",
          letterSpacing: "0.5px",
        }}
      >
        Notifiche
      </Typography>
      <Paper
        elevation={3}
        sx={{
          p: isMobile ? 2 : 3,
          borderRadius: "20px",
          border: "1px solid #e2e8f0",
        }}
      >
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          mb={2}
          columnGap={2}
          rowGap={1.5}
        >
          <Box
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            gap={1}
            sx={{ flex: "1 1 auto", minWidth: 0 }}
          >
            <Chip
              label="Tutti"
              size="small"
              onClick={() => setTypeFilter(null)}
              color={typeFilter == null ? "primary" : "default"}
              variant={typeFilter == null ? "filled" : "outlined"}
              sx={{
                fontWeight: 700,
                fontFamily: "inherit",
                borderColor: "#0b3d87",
              }}
            />
            {TYPE_FILTER_ENTRIES.map(({ typeKey, label }) => (
              <Chip
                key={typeKey}
                label={label}
                size="small"
                onClick={() => setTypeFilter(typeKey)}
                color={typeFilter === typeKey ? "primary" : "default"}
                variant={typeFilter === typeKey ? "filled" : "outlined"}
                sx={{
                  fontWeight: 700,
                  fontFamily: "inherit",
                  borderColor: "#0b3d87",
                }}
              />
            ))}
          </Box>
          <Box sx={{ flex: "0 0 auto", marginLeft: { xs: 0, sm: "auto" } }}>
            <Link
              component="button"
              type="button"
              underline="none"
              onClick={() => setViewArchived((v) => !v)}
              sx={navLinkSx}
            >
              {viewArchived ? "Vedi non archiviate" : "Vedi archiviate"}
            </Link>
          </Box>
        </Box>
        <NotificheListPanel
          items={filteredForDisplay}
          variant={variant}
          isInitialLoading={isInitialLoading}
          isLoadingMore={isLoadingMore}
          loadMoreDisabled={loadMoreDisabled}
          emptyMessage={emptyMessage}
          onOpen={openDialog}
          onToggleRead={handleToggleRead}
          onArchive={handleArchive}
          onDelete={setDeleteId}
          onRestore={handleRestore}
          onLoadMore={loadMore}
        />
      </Paper>

      <DialogNotifiche
        open={Boolean(dialogNotif)}
        onClose={() => setDialogNotif(null)}
        notification={dialogNotif}
        typeLabel={dialogTypeLabel}
        variant={variant}
        onToggleRead={handleToggleRead}
        onArchive={handleArchive}
        onDelete={setDeleteId}
        onRestore={handleRestore}
        readOnly={false}
      />

      <ConfirmationModal
        open={deleteId != null}
        onClose={() => setDeleteId(null)}
        title="Eliminare questa notifica?"
        subtitle=""
        message="La notifica verrà rimossa definitivamente dall'elenco."
        onConfirm={() => confirmDelete(deleteId)}
      />
    </Box>
  );
}
 